import { OrderMeal, RecentOrFavoriteOrderMeal, PreConfiguredMeal, MenuItemLookupResult, OrderMealEntree, RecentOrFavoriteEntree, RecentOrFavoriteItem, OrderMealItem, PreConfiguredMealDrink, PreConfiguredMealItem, PreConfiguredMealEntree, PortionItemType, ItemCustomizationId, GeneralItemType, RecentOrFavoriteEntreeContents, PreConfiguredMealEntreeContent, UpsellItem } from '@/models';
import { AppState } from '@/store';
import { round, reduce } from 'lodash';
import { Store } from 'vuex';
import { AnalyticsMeal, AnalyticsUpsellType, MerchVars, Product } from '../models';

type Meal = OrderMeal | RecentOrFavoriteOrderMeal | PreConfiguredMeal;
type Entree = OrderMealEntree | RecentOrFavoriteEntree;
enum ProductDetails {
  Normal = 'Normal',
}

export class ProductListItem implements Product {
  SKU?: string;
  name?: string;
  priceTotal?: number;
  quantity?: number;
  _merchVars?: MerchVars;

  static getProductListItems(meals: AnalyticsMeal, store: Store<AppState>, isDelivery: boolean): Product[] {
    let productListItems: Product[] = [];
    (Array.isArray(meals) ? meals : [meals])?.map(meal => productListItems = [...productListItems, ...ProductListItem.formatProduct(meal, store, isDelivery)]);
    return productListItems;
  }

  static formatProduct(meal: OrderMeal | RecentOrFavoriteOrderMeal | PreConfiguredMeal, store: Store<AppState>, isDelivery: boolean): Product[] {
    let productListItems = [];
    let product = {
      _merchVars: {
        topThingsChoice: '',
        otherOptions: '',
        includedSides: '',
        drinks: '',
        riceChoice: '',
        beansChoice: '',
        productName: ''
      },
    } as Product;
    let menuItemLookupMap = store.state.menu.menuItemLookupMap;
    // When a user selects an upsell item, we dummy up a meal, and throw all sides, drinks, etc under it
    // To know if an item (i.e. chips) is an upsell item, we need to see if the meal it falls under is the upsell meal
    const upsellMealId = store.state.order.upsellMeal?.mealId;
    let isUpsellMeal = upsellMealId ? meal.mealId == upsellMealId : false;

    (meal as OrderMeal | RecentOrFavoriteOrderMeal).entrees?.forEach((entree: OrderMealEntree | RecentOrFavoriteEntree) => {
      this.formatEntree(entree, menuItemLookupMap, product, isDelivery);
    });

    this.formatPceEntree((meal as PreConfiguredMeal)?.entree, menuItemLookupMap, product, isDelivery);

    meal.drinks?.forEach((drink: RecentOrFavoriteItem | OrderMealItem | PreConfiguredMealDrink) => {
      let drinkItem = drink as RecentOrFavoriteItem | OrderMealItem;
      let pceDrinkItem = drink as PreConfiguredMealDrink;

      if (!product.priceTotal) {
        product.priceTotal = 0;
      }

      if (product && product._merchVars) {
        const item = menuItemLookupMap?.get(drinkItem.menuItemId ?? pceDrinkItem.itemId);
        product._merchVars.drinks += `${product._merchVars.drinks ? '~' : ''}${drinkItem.menuItemName ?? pceDrinkItem.itemName}`;
        product.priceTotal += (isDelivery ? item?.unitDeliveryPrice : item?.unitPrice) ?? 0;

        const drinkProduct = {
          SKU: drinkItem.menuItemId ?? pceDrinkItem.itemId,
          name: drinkItem.menuItemId ?? pceDrinkItem.itemId,
          quantity: drinkItem.quantity,
          priceTotal: ((isDelivery ? item?.unitDeliveryPrice : item?.unitPrice) ?? 0) * drinkItem.quantity,
          _merchVars: { productName: drinkItem.menuItemName ?? pceDrinkItem.itemName } as MerchVars
        };
        
        this.formatUpsellData(drinkProduct, isUpsellMeal, store.state.adobeTarget.displayedUpsellItems, drinkItem.menuItemId ?? pceDrinkItem.itemId);

        productListItems.push(drinkProduct);
      }
    });

    meal.sides?.forEach((side: RecentOrFavoriteItem | OrderMealItem | PreConfiguredMealItem) => {
      let sideItem = side as RecentOrFavoriteItem | OrderMealItem;
      let pceSideItem = side as PreConfiguredMealItem;

      if (!product.priceTotal) {
        product.priceTotal = 0;
      }

      if (product && product._merchVars) {
        const item = menuItemLookupMap?.get(sideItem?.menuItemId ?? pceSideItem?.itemId);
        product._merchVars.includedSides += `${product._merchVars.includedSides ? '~' : ''}${sideItem?.menuItemName ?? pceSideItem?.itemName}`;
        product.priceTotal += (isDelivery ? item?.unitDeliveryPrice : item?.unitPrice) ?? 0;

        const sideProduct = {
          SKU: sideItem.menuItemId ?? pceSideItem.itemId,
          name: sideItem.menuItemId ?? pceSideItem.itemId,
          quantity: sideItem.quantity ?? pceSideItem.quantity,
          priceTotal: ((isDelivery ? item?.unitDeliveryPrice : item?.unitPrice) ?? 0) * sideItem.quantity,
          _merchVars: { productName: sideItem.menuItemName ?? pceSideItem.itemName } as MerchVars,
        };

        this.formatUpsellData(sideProduct, isUpsellMeal, store.state.adobeTarget.displayedUpsellItems, sideItem.menuItemId ?? pceSideItem.itemId);

        productListItems.push(sideProduct);
      }
    });

    product.priceTotal = round(product.priceTotal ?? 0, 2);

    if (product.SKU) {
      productListItems.push(product);
      this.formatUpsellData(product, isUpsellMeal, store.state.adobeTarget.displayedUpsellItems, product.SKU);
    }


    return productListItems;
  }

  private static formatPceEntree(entree: PreConfiguredMealEntree, menuItemLookupMap: Map<string, MenuItemLookupResult> | null, product: Product, isDelivery: boolean) {
    if (!entree) {
      return;
    }

    const menuItem = menuItemLookupMap?.get(entree.itemId);

    // set up product props
    product.SKU = entree.itemId ?? '';
    product.name = entree.itemId;
    product.quantity = 1;
    product.priceTotal =
      ((isDelivery ? menuItem?.unitDeliveryPrice : menuItem?.unitPrice) ?? 0) +
      reduce(
        entree.contents,
        (a, b) => {
          const price = isDelivery ? menuItemLookupMap?.get(b.itemId)?.unitDeliveryPrice : menuItemLookupMap?.get(b.itemId)?.unitPrice;
          return a + (price ?? 0);
        },
        0,
      );

    if (product && product._merchVars) {
      product._merchVars.productName = entree.itemName;
    }

    // Format Primary filling portion
    const portion = entree.contents?.find(
      item =>
        menuItemLookupMap?.get(item.itemId)?.menuItemType == PortionItemType.HalfPortion || menuItemLookupMap?.get(item.itemId)?.menuItemType == PortionItemType.ExtraPortion,
    );
    const portionType = menuItemLookupMap?.get(portion?.itemId ?? '')?.menuItemType ?? '';
    this.formatPrimaryFillingPortion(portionType, portion, product, menuItem?.primaryFillingName ?? '', portion?.itemName);

    // Format Entree Contents
    entree.contents?.forEach(content => {
      const menuItemType = menuItemLookupMap?.get(content.itemId)?.menuItemType ?? '';
      const customId = content.customizationId;
      this.formatEntreeContents(product, content.itemName, content.itemId, menuItemType, customId);
    });
  }

  private static formatEntree(entree: Entree, menuItemLookupMap: Map<string, MenuItemLookupResult> | null, product: Product, isDelivery: boolean) {
    if (!entree) {
      return;
    }

    const menuItem = menuItemLookupMap?.get(entree.menuItemId);

    // Set up product properties
    product.SKU = entree.menuItemId ?? '';
    product.name = entree.menuItemId ?? '';
    product.quantity = 1;
    product.priceTotal =
      ((isDelivery ? menuItem?.unitDeliveryPrice : menuItem?.unitPrice) ?? 0) +
      reduce(
        entree.contents,
        (a, b) => {
          const price = isDelivery ? menuItemLookupMap?.get(b.menuItemId)?.unitDeliveryPrice : menuItemLookupMap?.get(b.menuItemId)?.unitPrice;
          return a + (price ?? 0);
        },
        0,
      );

    if (product && product._merchVars) {
      product._merchVars.productName = entree.menuItemName || menuItem?.menuItemName;
    }

    // Format portion size of primary filling
    const portion = entree.contents?.find(
      item =>
        menuItemLookupMap?.get(item.menuItemId)?.menuItemType == PortionItemType.HalfPortion ||
        menuItemLookupMap?.get(item.menuItemId)?.menuItemType == PortionItemType.ExtraPortion,
    );
    const portionType = menuItemLookupMap?.get(portion?.menuItemId ?? '')?.menuItemType ?? '';
    this.formatPrimaryFillingPortion(portionType, portion, product, menuItem?.primaryFillingName ?? '', portion?.menuItemName);

    // Format all entree contents
    entree.contents?.forEach(content => {
      const menuItemType = menuItemLookupMap?.get(content.menuItemId)?.menuItemType ?? '';
      const customId = content.customizationId;
      this.formatEntreeContents(product, content.menuItemName, content.menuItemId, menuItemType, customId);
    });
  }

  private static formatEntreeContents(product: Product, menuItemName: string, menuItemId: string, menuItemType: string, customizationId: number) {
    const customId = customizationId;
    const customType = ItemCustomizationId[customId];
    product._merchVars = product._merchVars ?? {};

    switch (menuItemType) {
      case GeneralItemType.Rice:
        if (!customId) {
          product._merchVars.riceChoice = `${menuItemName}~${ProductDetails.Normal}`;
        } else {
          product._merchVars.riceChoice += `${product._merchVars.riceChoice ? '~' : ''}${menuItemName}${customId == ItemCustomizationId.Half ? '-1/2' : '~' + customType}${product._merchVars.riceChoice ? '~Half Rice' : ''}`;
        }
        break;
      case GeneralItemType.Beans:
        if (!customId) {
          product._merchVars.beansChoice = `${menuItemName}~${ProductDetails.Normal}`;
        } else {
          product._merchVars.beansChoice += `${product._merchVars.beansChoice ? '~' : ''}${menuItemName}${customId == ItemCustomizationId.Half ? '-1/2' : '~' + customType}${product._merchVars.beansChoice ? '~Half Beans' : ''}`;
        }
        break;
      case GeneralItemType.Toppings:
        product._merchVars.topThingsChoice += `${product._merchVars.topThingsChoice ? '~' : ''}${
          customId ? menuItemName + ':' + customType : menuItemName + ':' + ProductDetails.Normal
        }`;
        break;
      case GeneralItemType.Option:
        product._merchVars.otherOptions += `${product._merchVars.otherOptions ? '~' : ''}${menuItemName}`;
        break;
      // Kid items have side and beverage as entree contents
      case GeneralItemType.Side:
        product._merchVars.includedSides += `${product._merchVars.includedSides ? '~' : ''}${menuItemName}`;
        break;
      case GeneralItemType.Beverage:
        product._merchVars.drinks += `${product._merchVars.drinks ? '~' : ''}${menuItemName}`;
        break;
      case GeneralItemType.Tortillas:
        product._merchVars.otherOptions += `${product._merchVars.otherOptions ? '~' : ''}${menuItemName}`;
    }
  }

  private static formatPrimaryFillingPortion(
    portionType: string,
    portion: RecentOrFavoriteEntreeContents | PreConfiguredMealEntreeContent | undefined,
    product: Product,
    primaryFillingName: string,
    portionName: string | undefined,
  ) {
    if (!product._merchVars) {
      return;
    }

    if (!portion) {
      product._merchVars.proteinChoice = `${primaryFillingName}~${ProductDetails.Normal}`;
    } else if (portionType == PortionItemType.HalfPortion) {
      product._merchVars.proteinChoice = `${primaryFillingName}-1/2~${portionName?.replace('Half', '').trim()}-1/2~Half Protein`;
    } else if (portionType == PortionItemType.ExtraPortion) {
      product._merchVars.proteinChoice = `${primaryFillingName}~${portionName?.replace('Extra ', '')}~Double Protein`;
    }
  }

  private static formatUpsellData(product: Product, isUpsellMeal: boolean, upsellItems: UpsellItem[], cmgId: string) {
    if (product._merchVars) {
      if (isUpsellMeal) {
        if (upsellItems.length > 0) {
          const matchingUpsellItem = upsellItems.find(i => i.cmgId == cmgId);
          product._merchVars.upsellType = matchingUpsellItem?.upsellType ?? AnalyticsUpsellType.Static;
        } else {
          product._merchVars.upsellType = AnalyticsUpsellType.Static;
        }
        product._merchVars.upsellAmount = product.priceTotal;
      } else {  
        product._merchVars.upsellType = AnalyticsUpsellType.NotUpsell;
      }  
    } 
  }
}